import { MonitoringSystemType, TimeUnit } from '@serverfarm/nocd-commons';
import logdown from 'logdown';
import { Maybe } from 'monet';
import React from 'react';
import * as Icon from 'react-bootstrap-icons';
import { Typeahead } from 'react-bootstrap-typeahead';
// @ts-ignore
import EllipsisText from 'react-ellipsis-text';
import { connect } from 'react-redux';
import { returntypeof } from 'react-redux-typescript';
import {
  Alert as BsAlert,
  Button,
  Form,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  TabContent,
  TabPane,
} from 'reactstrap';
import { bindActionCreators, Dispatch } from 'redux';

import { State } from '../../reducers';
import { fetchCompanies } from '../user';
import { DraggableModal } from '../utils';

import {
  completeMonitoringSystemEdit,
  editMonitoringSystem,
  editMonitoringSystemSite,
  editMonitoringSystemName,
  editMonitoringSystemCode,
  editMonitoringSystemType,
  editMonitoringSystemFrequency,
  editMonitoringSystemTimeUnit,
  editMonitoringSystemSev1Def,
  editMonitoringSystemSev2Def,
  editMonitoringSystemSev3Def,
  editMonitoringSystemSev4Def,
  editMonitoringSystemSev5Def,
  editMonitoringSystemNormalDef,
  editMonitoringSystemOffnormalDef,
  editMonitoringSystemIgnoreDef,
  editMonitoringSystemRegex,
  saveMonitoringSystem,
} from './data/monitoring-systems/actions';
import ParsingSandbox from './ParsingSandbox';

const logger = logdown('components:MonitoringSystemEditor');

type EditorTab = 'general' | 'parsing';

/** Two equal columns, so a form of short fields does not become a page of scrolling. */
const TWO_COLUMNS: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '1.5rem' };
const FULL_WIDTH: React.CSSProperties = { gridColumn: '1 / -1' };

/**
 * Which keywords a parsing definition is missing. Mirrors `missingParsingDefinitions` in mep-commons: wingfoal parses
 * a Monitoring System dynamically once it has severity keywords and a Normal or Offnormal keyword, so a form with
 * neither quietly leaves the site on its hardcoded parser. The editor says so instead.
 *
 * The regex is not counted (decided 2026-09-10): a system with its keywords and no regex parses as it always did,
 * taking its source from the old extraction, and a regex is an improvement made deliberately on top of that.
 */
/**
 * What is wrong with each regex in the list, if anything. Mirrors `validateParserDefinitions` in mep-commons: a regex
 * has to compile and has to tag all three parts, because the tags are what say which token carries the severity and
 * which the state. An incomplete one is skipped by wingfoal, so saving it would leave a definition on screen that
 * production quietly ignores. The editor refuses instead.
 */
const regexProblems = (regex?: string[]): string[] => {
  const problems: string[] = [];
  (regex || []).forEach((pattern, i) => {
    const where = `Regex ${i + 1}`;
    if (!pattern || !pattern.trim()) {
      problems.push(`${where} is empty`);
      return;
    }
    try {
      new RegExp(pattern);
    } catch (error) {
      problems.push(`${where} does not compile: ${(error as Error).message}`);
      return;
    }
    const missing = ['source', 'severity', 'state'].filter((tag) => !pattern.includes(`(?<${tag}>`));
    if (missing.length) {
      problems.push(`${where} does not tag ${missing.join(', ')}; a regex needs all three`);
    }
  });
  return problems;
};

const missingParsingParts = (parser?: { [k: string]: any }): string[] => {
  const filled = (value?: string): boolean =>
    (value || '')
      .split(' | ')
      .map((v) => v.trim())
      .filter((v) => v.length > 0).length > 0;
  const missing: string[] = [];
  if (!['sev1_def', 'sev2_def', 'sev3_def', 'sev4_def', 'sev5_def'].some((box) => filled(parser && parser[box]))) {
    missing.push('severity keywords');
  }
  if (!filled(parser && parser.normal_def) && !filled(parser && parser.offnormal_def)) {
    missing.push('Normal or Offnormal keywords');
  }
  return missing;
};

class Component extends React.Component<Props, { activeTab: EditorTab }> {
  constructor(props: Props) {
    super(props);
    this.state = { activeTab: 'general' };
  }

  setTab = (activeTab: EditorTab) => {
    this.setState({ activeTab });
  };

  public render() {
    const generateRandomCode = (): string => {
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < 8; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        result += characters[randomIndex];
      }
      return result;
    };

    const parser = this.props.monitoringSystemEditing.monitoringSystem.map((t: any) => t.parser || {}).getOrElse({});
    const missing = missingParsingParts(parser);
    const badRegexes = regexProblems(parser && (parser.regex as string[]));

    return (
      <DraggableModal
        data-testid="monitoringsystem-editor-modal"
        id="monitoringsystem-editor-modal"
        size="xl"
        scrollable
        isOpen={this.props.monitoringSystemEditing.isInProgress}
        toggle={this.cancel.bind(this)}
        onUnmountWhileOpen={this.cancel.bind(this)}
      >
        <ModalHeader data-testid="monitoringsystem-editor-header" id="monitoringsystem-editor-header" toggle={this.cancel.bind(this)}>
          <EllipsisText
            text={`Edit '${this.props.monitoringSystemEditing.monitoringSystem.map((t: any) => t.name || '...').getOrElse('...')}'`}
            length={45}
          />
        </ModalHeader>
        {/* No padding at the top: the sandbox result table has a sticky header, which sticks to the body's padding box,
            so a padding-top here would leave a strip where its rows show above the header while scrolling. */}
        <ModalBody className="pt-0">
          <Form onSubmit={this.submit.bind(this)}>
            <Nav tabs className="mb-3 pt-3">
              <NavItem>
                <NavLink
                  data-testid="monitoringsystem-editor-tab-general"
                  href="#"
                  active={this.state.activeTab === 'general'}
                  onClick={(e) => {
                    e.preventDefault();
                    this.setTab('general');
                  }}
                >
                  General
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  data-testid="monitoringsystem-editor-tab-parsing"
                  href="#"
                  active={this.state.activeTab === 'parsing'}
                  onClick={(e) => {
                    e.preventDefault();
                    this.setTab('parsing');
                  }}
                >
                  {/* No badge: the line under the tabs already says what is missing and what it means. */}
                  Parsing
                </NavLink>
              </NavItem>
            </Nav>
            <TabContent activeTab={this.state.activeTab}>
              <TabPane tabId="general">
                <div style={TWO_COLUMNS}>
                  <FormGroup>
                    <Label for="name">Name</Label>
                    <Input
                      data-testid="monitoringsystem-editor-name-input"
                      id="name"
                      type="text"
                      value={this.props.monitoringSystemEditing.monitoringSystem.map((t) => t.name || '').getOrElse('')}
                      onChange={(e) => this.props.editMonitoringSystemName(e.target.value)}
                    />
                    {/* <FormFeedback invalid="true">You will not be able to see this</FormFeedback> */}
                    <FormText>Type name of the Monitoring System for future reference</FormText>
                  </FormGroup>
                  <FormGroup>
                    <Label for="report-editor-company">Site</Label>
                    {(() => {
                      let ref: any;
                      return (
                        <Typeahead
                          id="report-editor-company"
                          onBlur={() => ref.hideMenu()}
                          ref={(el) => (ref = el)}
                          isLoading={this.props.sitesListing.isInProgress}
                          onChange={(selected) => this.props.editMonitoringSystemSite(selected[0])}
                          options={this.props.sitesListing.sites.getOrElse([]).map((s) => s.monitorSiteId)}
                          emptyLabel="No Sites found"
                          placeholder="Start typing to find a Site by name"
                          defaultSelected={this.props.monitoringSystemEditing.monitoringSystem
                            .flatMap((t) => Maybe.fromUndefined(t.site))
                            .map((t) => [t])
                            .getOrElse([])}
                          // isValid={this.props.reportEditing.report.flatMap(report => report.company).isSome()}
                          isInvalid={this.props.monitoringSystemEditing.monitoringSystem.flatMap((t) => Maybe.fromUndefined(t.site)).isNone()}
                          // disabled={this.props.monitoringSystemEditing.monitoringSystem.map((t) => !!t.id).contains(true)}
                        />
                      );
                    })()}
                    <FormText>Choose a Site to bind the Monitoring System </FormText>
                  </FormGroup>
                  <FormGroup>
                    <Label for="report-editor-type">Provider</Label>
                    {(() => {
                      let ref: any;
                      return (
                        <Typeahead
                          id="report-editor-type"
                          onBlur={() => ref.hideMenu()}
                          ref={(el) => (ref = el)}
                          onChange={(selected) => this.props.editMonitoringSystemType(selected[0])}
                          options={Object.values(MonitoringSystemType)}
                          emptyLabel="No Type found"
                          placeholder="Start typing to find a Type by name"
                          defaultSelected={this.props.monitoringSystemEditing.monitoringSystem
                            .flatMap((t) => Maybe.fromUndefined(t.type))
                            .map((t) => [t])
                            .getOrElse([])}
                          // isValid={this.props.reportEditing.report.flatMap(report => report.company).isSome()}
                          isInvalid={this.props.monitoringSystemEditing.monitoringSystem.flatMap((t) => Maybe.fromUndefined(t.type)).isNone()}
                          // disabled={this.props.monitoringSystemEditing.monitoringSystem.map((t) => !!t.id).contains(true)}
                        />
                      );
                    })()}
                    <FormText>Choose a Site to bind the Monitoring System </FormText>
                  </FormGroup>
                  <FormGroup>
                    <Label for="name">Code</Label>
                    <InputGroup>
                      <Input
                        data-testid="monitoringsystem-editor-name-input"
                        id="name"
                        type="text"
                        value={this.props.monitoringSystemEditing.monitoringSystem.map((t) => t.code || '').getOrElse('')}
                        onChange={(e) => this.props.editMonitoringSystemCode(e.target.value)}
                        //disabled={this.props.monitoringSystemEditing.monitoringSystem.map((t) => !!t.id).contains(true)}
                      />
                      <InputGroupAddon addonType="append">
                        <InputGroupText
                          className="text-filter"
                          style={{
                            cursor: 'pointer',
                            background: 'white',
                            border: '1px solid #dddddd',
                            borderLeft: '0',
                          }}
                        >
                          <>
                            <Icon.Shuffle
                              id={`code-generator`}
                              style={{ marginRight: '6px' }}
                              onClick={() => {
                                this.props.editMonitoringSystemCode(generateRandomCode());
                              }}
                            />
                          </>
                        </InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                    {/* <FormFeedback invalid="true">You will not be able to see this</FormFeedback> */}
                    <FormText>Type the code linked to Monitoring System</FormText>
                  </FormGroup>
                  <FormGroup>
                    <Label for="frequency">Frequency</Label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Input
                        data-testid="monitoringsystem-editor-frequency-input"
                        id="frequency"
                        type="number"
                        min="0"
                        value={this.props.monitoringSystemEditing.monitoringSystem.map((t) => Math.abs(t.frequency) || '').getOrElse('')}
                        onChange={(e) => this.props.editMonitoringSystemFrequency(Math.abs(Number(e.target.value)))}
                        style={{ width: '110px' }}
                      />
                      <Input
                        type="select"
                        id="timeUnit"
                        value={this.props.monitoringSystemEditing.monitoringSystem.map((t) => t.timeUnit || 'HOURS').getOrElse('HOURS' as TimeUnit)}
                        onChange={(e) => this.props.editMonitoringSystemTimeUnit(e.target.value as TimeUnit)}
                        style={{ width: '140px', marginLeft: '10px' }}
                      >
                        <option value="HOURS">HOURS</option>
                        <option value="MINUTES">MINUTES</option>
                      </Input>
                    </div>
                    <FormText>
                      Type the amount of time and select the unit (HOURS or MINUTES) that it should take to receive a new event from this Monitoring
                      System
                    </FormText>
                  </FormGroup>
                </div>
              </TabPane>
              <TabPane tabId="parsing">
                {badRegexes.length ? (
                  <BsAlert color="danger" className="py-2">
                    {badRegexes.join('. ')}. Saving is blocked until it is fixed: wingfoal skips a regex that does not tag all three parts, so this
                    definition would look configured while production ignored it.
                  </BsAlert>
                ) : null}
                {missing.length ? (
                  <FormText className="d-block mb-3">
                    Missing {missing.join(' and ')}. Until both are set, e-mails from this Monitoring System keep going through its hardcoded parser.
                  </FormText>
                ) : (parser && ((parser.regex as string[]) || []).length) || 0 ? (
                  <FormText className="d-block mb-3">
                    E-mails from this Monitoring System are parsed with these definitions, and the source comes from the regex below.
                  </FormText>
                ) : (
                  <FormText className="d-block mb-3">
                    E-mails from this Monitoring System are parsed with these keywords. With no regex the source comes from the old extraction, which
                    strips labels and timestamps off the message; add one below to decide it yourself.
                  </FormText>
                )}
                <div style={TWO_COLUMNS}>
                  <FormGroup>
                    <Label for="name">Severity Level 1</Label>
                    <Input
                      data-testid="monitoringsystem-editor-sev1-def-input"
                      id="name"
                      type="text"
                      value={parser.sev1_def || ''}
                      onChange={(e) => this.props.editMonitoringSystemSev1Def(e.target.value)}
                    />
                    {/* <FormFeedback invalid="true">You will not be able to see this</FormFeedback> */}
                  </FormGroup>
                  <FormGroup>
                    <Label for="name">Severity Level 2</Label>
                    <Input
                      data-testid="monitoringsystem-editor-sev2-input"
                      id="name"
                      type="text"
                      value={parser.sev2_def || ''}
                      onChange={(e) => this.props.editMonitoringSystemSev2Def(e.target.value)}
                    />
                    {/* <FormFeedback invalid="true">You will not be able to see this</FormFeedback> */}
                  </FormGroup>
                  <FormGroup>
                    <Label for="name">Severity Level 3</Label>
                    <Input
                      data-testid="monitoringsystem-editor-sev3-input"
                      id="name"
                      type="text"
                      value={parser.sev3_def || ''}
                      onChange={(e) => this.props.editMonitoringSystemSev3Def(e.target.value)}
                    />
                    {/* <FormFeedback invalid="true">You will not be able to see this</FormFeedback> */}
                  </FormGroup>
                  <FormGroup>
                    <Label for="name">Severity Level 4</Label>
                    <Input
                      data-testid="monitoringsystem-editor-sev4-input"
                      id="name"
                      type="text"
                      value={parser.sev4_def || ''}
                      onChange={(e) => this.props.editMonitoringSystemSev4Def(e.target.value)}
                    />
                    {/* <FormFeedback invalid="true">You will not be able to see this</FormFeedback> */}
                  </FormGroup>
                  <FormGroup>
                    <Label for="name">Severity Level 5</Label>
                    <Input
                      data-testid="monitoringsystem-editor-sev5-input"
                      id="name"
                      type="text"
                      value={parser.sev5_def || ''}
                      onChange={(e) => this.props.editMonitoringSystemSev5Def(e.target.value)}
                    />
                    {/* <FormFeedback invalid="true">You will not be able to see this</FormFeedback> */}
                  </FormGroup>
                  <FormGroup>
                    <Label for="name">Normal State</Label>
                    <Input
                      data-testid="monitoringsystem-editor-normal-def-input"
                      id="name"
                      type="text"
                      value={parser.normal_def || ''}
                      onChange={(e) => this.props.editMonitoringSystemNormalDef(e.target.value)}
                    />
                    {/* <FormFeedback invalid="true">You will not be able to see this</FormFeedback> */}
                  </FormGroup>
                  <FormGroup>
                    <Label for="name">Offnormal State</Label>
                    <Input
                      data-testid="monitoringsystem-editor-normal-def-input"
                      id="name"
                      type="text"
                      value={parser.offnormal_def || ''}
                      onChange={(e) => this.props.editMonitoringSystemOffnormalDef(e.target.value)}
                    />
                    {/* <FormFeedback invalid="true">You will not be able to see this</FormFeedback> */}
                  </FormGroup>
                  <FormGroup style={FULL_WIDTH}>
                    <Label for="ignore_def">
                      Ignore <small className="text-muted">(heartbeats, test e-mails)</small>
                    </Label>
                    <Input
                      data-testid="monitoringsystem-editor-ignore-def-input"
                      id="ignore_def"
                      type="text"
                      value={parser.ignore_def || ''}
                      onChange={(e) => this.props.editMonitoringSystemIgnoreDef(e.target.value)}
                    />
                    <FormText>An e-mail containing any of these keywords creates no alert and is stored in Notifications Status as IGNORED.</FormText>
                  </FormGroup>
                </div>
                {this.props.monitoringSystemEditing.monitoringSystem
                  .map((t) => (
                    <ParsingSandbox
                      key={t.id || 'new'}
                      monitoringSystemId={t.id}
                      site={t.site}
                      type={t.type}
                      parser={t.parser}
                      onRegexChange={(regex) => this.props.editMonitoringSystemRegex(regex)}
                    />
                  ))
                  .orNull()}
              </TabPane>
            </TabContent>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            data-testid="monitoringsystem-editor-save-button"
            id="monitoringsystem-editor-save-button"
            color="primary"
            disabled={!this.props.monitoringSystemEditing.monitoringSystem || this.props.monitoringSystemSaving.isInProgress || !!badRegexes.length}
            title={badRegexes.length ? badRegexes.join('; ') : undefined}
            onClick={this.submit.bind(this)}
          >
            {this.props.monitoringSystemSaving.isInProgress ? <Spinner size="sm" /> : 'Save'}
          </Button>{' '}
          <Button
            data-testid="monitoringsystem-editor-cancel-button"
            id="monitoringsystem-editor-cancel-button"
            color="secondary"
            disabled={this.props.monitoringSystemSaving.isInProgress}
            onClick={this.cancel.bind(this)}
          >
            Cancel
          </Button>
        </ModalFooter>
      </DraggableModal>
    );
  }

  protected submit(e: any) {
    e.preventDefault();
    this.props.monitoringSystemEditing.monitoringSystem.forEach((monitoringSystem) =>
      this.props.saveMonitoringSystem(monitoringSystem, !monitoringSystem.id),
    );
  }

  protected cancel(e?: any) {
    e?.preventDefault();
    this.props.completeMonitoringSystemEdit();
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>) =>
  bindActionCreators(
    {
      startMonitoringSystemEdit: editMonitoringSystem,
      editMonitoringSystemName,
      editMonitoringSystemSite,
      editMonitoringSystemCode,
      editMonitoringSystemType,
      editMonitoringSystemFrequency,
      editMonitoringSystemTimeUnit,
      editMonitoringSystemSev1Def,
      editMonitoringSystemSev2Def,
      editMonitoringSystemSev3Def,
      editMonitoringSystemSev4Def,
      editMonitoringSystemSev5Def,
      editMonitoringSystemNormalDef,
      editMonitoringSystemOffnormalDef,
      editMonitoringSystemIgnoreDef,
      editMonitoringSystemRegex,
      completeMonitoringSystemEdit,
      saveMonitoringSystem,
      fetchCompanies,
    },
    dispatch,
  );

const mapStateToProps = (state: State) => ({
  user: state.user,
  monitoringSystemEditing: state.monitoringSystemEditing,
  monitoringSystemSaving: state.monitoringSystemSaving,
  sitesListing: state.sitesListing,
});

const stateProps = returntypeof(mapStateToProps);
const dispatchProps = returntypeof(mapDispatchToProps);
type Props = typeof stateProps & typeof dispatchProps;

// @ts-ignore
export default connect<typeof stateProps, typeof dispatchProps, any>(mapStateToProps, mapDispatchToProps)(Component);
